import React, { useState, useEffect, useCallback, useMemo } from 'react';

import AILoadingIndicator from '../components/AILoadingIndicator';
const API_URL = process.env.REACT_APP_API_URL || 'https://stock-api-production-913a.up.railway.app';

// ─── 12 項評分標籤 ───
const ITEMS = {
  safety: [
    { key: 'scoring_safety_1', name: '存貨/應收管理' },
    { key: 'scoring_safety_2', name: '財務透明度' },
    { key: 'scoring_safety_3', name: '有息負債比' },
    { key: 'scoring_safety_4', name: '餘絀現金' },
  ],
  profit: [
    { key: 'scoring_profit_1', name: '盈餘含金量' },
    { key: 'scoring_profit_2', name: '業外收支' },
    { key: 'scoring_profit_3', name: '營益率+年增' },
    { key: 'scoring_profit_4', name: '費用率' },
    { key: 'scoring_profit_5', name: '每股盈餘' },
  ],
  future: [
    { key: 'scoring_future_1', name: '毛利率' },
    { key: 'scoring_future_2', name: '資本投入' },
    { key: 'scoring_future_3', name: '營收成長' },
  ],
};

const FACE_META = {
  safety: { icon: '🛡️', label: '安全性', color: '#4C9BB8', bgGlow: 'rgba(76, 155, 184, 0.15)' },
  profit: { icon: '💰', label: '獲利性', color: '#f5c518', bgGlow: 'rgba(245, 197, 24, 0.15)' },
  future: { icon: '🚀', label: '未來性', color: '#e54848', bgGlow: 'rgba(229, 72, 72, 0.15)' },
};

// ─── 12 項指標的公版說明（初始畫面用，無分數時顯示）───
const ITEM_DESC = {
  scoring_safety_1: '存貨、應收帳款管理欠妥，易造成資金呆滯及缺口。',
  scoring_safety_2: '帳務透明度高，提高財務的可信度。',
  scoring_safety_3: '有息負債高，易造成企業經營的財務壓力。',
  scoring_safety_4: '有餘絀現金，讓企業在資金調度上更顯游刃有餘。',
  scoring_profit_1: '除了帳面獲利，更要有實際賺錢的能力。',
  scoring_profit_2: '業外收支提高財務的不穩定度，讓獲利評估的可信度降低。',
  scoring_profit_3: '證明企業本業的獲利能力與強度。',
  scoring_profit_4: '除了開源，節流也是獲利的手段。',
  scoring_profit_5: '市場鎂光燈的焦點所在。',
  scoring_future_1: '一家公司產品在市場價格定價權的表現。',
  scoring_future_2: '企業永續經營的催化劑。',
  scoring_future_3: '企業一切成長的源頭。',
};

// ─── 評分顏色（分數越高越接近金色，越低越紅）───
const scoreColor = (s) => {
  if (s == null) return '#666';
  if (s >= 9) return '#FFD700';      // 金
  if (s >= 7.5) return '#22c55e';    // 綠
  if (s >= 6) return '#7ab0cc';      // 藍
  if (s >= 4) return '#f0a500';      // 橙
  return '#e54848';                  // 紅
};

const scoreLevel = (s) => {
  if (s == null) return '－';
  if (s >= 9) return '🏆 卓越';
  if (s >= 7.5) return '✅ 良好';
  if (s >= 6) return '👍 穩健';
  if (s >= 4) return '⚠️ 警示';
  return '🚨 危險';
};

export default function Scoreboard({ ticker: externalTicker, setTicker }) {
  // ─── State ───
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState(externalTicker || '');
  const [selectedName, setSelectedName] = useState('');

  const [scoreData, setScoreData] = useState(null);
  const [, setRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [, setExpandedItem] = useState(null);
  const [dupont, setDupont] = useState(null);
  const [dupontLoading, setDupontLoading] = useState(false);

  // ── 全市場 PR 排行榜 state ──
  const [rankings, setRankings] = useState(null);
  const [rankAspect, setRankAspect] = useState('total');
  const [rankSide, setRankSide] = useState('top');

  // ── 私房股勾選 state ──
  const [watchlistSet, setWatchlistSet] = useState(new Set());
  const watchlistRef = React.useRef(new Set());
  useEffect(() => { watchlistRef.current = watchlistSet; }, [watchlistSet]);

  const toggleWatchlist = useCallback((ticker, name) => {
    const _token = sessionStorage.getItem('wintime_admin_token') || '';
    const isAdmin = !!_token;
    const inList = watchlistRef.current.has(ticker);
    setWatchlistSet(prev => {
      const next = new Set(prev);
      inList ? next.delete(ticker) : next.add(ticker);
      return next;
    });
    window.dispatchEvent(new CustomEvent('watchlistChanged', {
      detail: { action: inList ? 'remove' : 'add', ticker, name }
    }));
    if (isAdmin) {
      const _tk = encodeURIComponent(_token);
      const url = inList
        ? `${API_URL}/api/watchlist/${ticker}?token=${_tk}`
        : `${API_URL}/api/watchlist/${ticker}?name=${encodeURIComponent(name || '')}&token=${_tk}`;
      fetch(url, { method: inList ? 'DELETE' : 'POST' }).catch(() => {});
    } else {
      try {
        const saved = JSON.parse(localStorage.getItem('wt_watchlist') || '[]');
        const updated = inList ? saved.filter(t => t !== ticker) : [...saved, ticker];
        localStorage.setItem('wt_watchlist', JSON.stringify(updated));
      } catch {}
    }
  }, []);

  // 載入私房股清單
  useEffect(() => {
    const isAdmin = !!sessionStorage.getItem('wintime_admin_token');
    if (isAdmin) {
      fetch(`${API_URL}/api/watchlist`)
        .then(r => r.json())
        .then(d => { if (Array.isArray(d)) setWatchlistSet(new Set(d.map(s => s.ticker))); })
        .catch(() => {});
    } else {
      try {
        const saved = JSON.parse(localStorage.getItem('wt_watchlist') || '[]');
        setWatchlistSet(new Set(saved));
      } catch {}
    }
  }, []);



  // 權重固定：安全 30 / 獲利 30 / 未來 40
  const weights = { safety: 30, profit: 30, future: 40 };

  // ─── 初始載入公司清單 + 規則 ───
  useEffect(() => {
    fetch(`${API_URL}/api/companies`).then(r => r.json()).then(d => setCompanies(Array.isArray(d) ? d : []));
    fetch(`${API_URL}/api/scoring-rules`).then(r => r.json()).then(d => {
      if (d.ok) {
        const map = {};
        (d.rules || []).forEach(r => { map[r.key] = r.content; });
        setRules(map);
      }
    });
  }, []);



  // ─── 初始載入：私房股清單 ───



  // ─── 外部 ticker 變動 ───
  useEffect(() => {
    if (externalTicker && externalTicker !== selectedTicker) {
      setSelectedTicker(externalTicker);
      const c = companies.find(c => c.ticker === externalTicker);
      if (c) setSelectedName(c.name);
    }
  }, [externalTicker, companies]);

  // ─── 搜尋（200ms debounce 避免打字時頻繁 filter 造成卡頓）───
  useEffect(() => {
    if (!query) { setResults([]); return; }
    const timer = setTimeout(() => {
      const q = query.toLowerCase();
      setResults(companies.filter(c =>
        c.ticker.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
      ).slice(0, 6));
    }, 200);
    return () => clearTimeout(timer);
  }, [query, companies]);

  // ─── 選股後：先試讀快取，沒快取自動跑 AI 評分 ───
  const loadFromCache = useCallback(async (ticker) => {
    if (!ticker) return;
    setLoading(true); setError(''); setScoreData(null); setExpandedItem(null);
    try {
      const r = await fetch(`${API_URL}/api/scoreboard/cache/${ticker}`);
      const d = await r.json();
      if (d.ok) {
        setScoreData(d);
      } else {
        setError('此股票尚未納入計分板');
      }
    } catch (e) {
      setError('讀取失敗：' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTicker) loadFromCache(selectedTicker);
  }, [selectedTicker, loadFromCache]);

  // ─── 面向分數 + 總分（直接讀 scoreboard cache）───
  const summary = useMemo(() => {
    if (!scoreData) return null;
    return {
      safety: scoreData.safety_score,
      profit: scoreData.profit_score,
      future: scoreData.future_score,
      total: scoreData.total_score,
    };
  }, [scoreData]);

  // ─── ticker 變動：自動抓杜邦資料（跟 AI 評分平行，不互相等待）───
  useEffect(() => {
    if (!selectedTicker) { setDupont(null); return; }
    setDupontLoading(true); setDupont(null);
    fetch(`${API_URL}/api/dupont/${selectedTicker}`)
      .then(r => r.json())
      .then(d => setDupont(d.ok ? d : null))
      .catch(() => setDupont(null))
      .finally(() => setDupontLoading(false));
  }, [selectedTicker]);

  // ── 全市場 PR 排行榜 mount 抓一次 ──
  useEffect(() => {
    fetch(`${API_URL}/api/scoreboard/rankings`)
      .then(r => r.json())
      .then(d => setRankings(d.ok ? d : null))
      .catch(() => setRankings(null));
  }, []);

  // ─── 選擇個股 ───
  const handleSelect = (c) => {
    setSelectedTicker(c.ticker);
    setSelectedName(c.name);
    setQuery(''); setResults([]);
    setTicker && setTicker(c.ticker);
  };

  // ─── 渲染 ───
  return (
    <div style={{ padding: 12, fontFamily: "'Rajdhani', 'Barlow', system-ui, sans-serif", maxWidth: 1400, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 18, fontSize: 26, fontWeight: 800, letterSpacing: 1.5 }}>
        🏆 股市 MLB <span style={{ color: '#7ab0cc', fontSize: 14, fontWeight: 400, marginLeft: 8 }}>全市場 PR + 投資規則自動評分</span>
      </h2>

      {/* 搜尋區 */}
      <div style={{ marginBottom: 16, background: '#0d1520', border: '1px solid #1e2a3a', borderRadius: 10, padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 300px', maxWidth: 400 }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && results.length > 0) {
                  e.preventDefault();
                  handleSelect(results[0]);
                }
              }}
              placeholder="🔍 輸入股票代號或名稱（按 Enter 直接選第一筆）..."
              autoComplete="off"
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid #2a3a4a', background: '#080d14', color: '#fff',
                fontSize: 14, fontFamily: 'monospace',
              }}
            />
            {results.length > 0 && (
              <div style={{
                position: 'absolute', top: 44, left: 0, right: 0, zIndex: 50,
                background: '#0d1520', border: '1px solid #2a3a4a', borderRadius: 8, maxHeight: 280, overflowY: 'auto',
              }}>
                {results.map(c => (
                  <div key={c.ticker} onClick={() => handleSelect(c)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #1a2535', display: 'flex', gap: 10 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#162030'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ color: '#4C9BB8', fontWeight: 700, minWidth: 56 }}>{c.ticker}</span>
                    <span style={{ color: '#ddd' }}>{c.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedTicker && (
            <div style={{ color: '#ddd', fontSize: 16 }}>
              <span style={{ color: '#4C9BB8', fontWeight: 700, marginRight: 8 }}>{selectedTicker}</span>
              <span style={{ color: '#fff', fontWeight: 600 }}>{selectedName}</span>
            </div>
          )}


        </div>

        {error && (
          <div style={{ marginTop: 10, padding: 10, background: '#3a1a1a', borderRadius: 6, color: '#ff8888', fontSize: 13 }}>
            ❌ {error}
          </div>
        )}
      </div>

      {/* 雷達圖 + 總分（直接顯示雷達圖，總分放圖下方） */}
      {summary && (
        <div style={{
          marginBottom: 16, background: '#0d1520', border: '1px solid #1e2a3a', borderRadius: 10, padding: 20,
        }}>
          {/* 雷達圖（12 項）*/}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {(() => {
              const cx = 210, cy = 210, R = 130;
              const ITEMS12 = [
                { key: 'scoring_safety_1', short: '存貨應收', face: 'safety' },
                { key: 'scoring_safety_2', short: '財務透明', face: 'safety' },
                { key: 'scoring_safety_3', short: '有息負債', face: 'safety' },
                { key: 'scoring_safety_4', short: '餘絀現金', face: 'safety' },
                { key: 'scoring_profit_1', short: '盈餘含金', face: 'profit' },
                { key: 'scoring_profit_2', short: '業外收支', face: 'profit' },
                { key: 'scoring_profit_3', short: '營益率',   face: 'profit' },
                { key: 'scoring_profit_4', short: '費用率',   face: 'profit' },
                { key: 'scoring_profit_5', short: 'EPS',       face: 'profit' },
                { key: 'scoring_future_1', short: '毛利率',   face: 'future' },
                { key: 'scoring_future_2', short: '資本投入', face: 'future' },
                { key: 'scoring_future_3', short: '營收成長', face: 'future' },
              ];
              const n = 12;
              const step = (2 * Math.PI) / n;
              const start = -Math.PI / 2;
              const angles = ITEMS12.map((_, i) => start + i * step);

              const vals = ITEMS12.map(it => {
                const fieldKey = it.key.replace('scoring_', '');
                const s = scoreData?.[fieldKey];
                return typeof s === 'number' ? s : 0;
              });

              const pt = (angle, ratio) => ({
                x: cx + R * ratio * Math.cos(angle),
                y: cy + R * ratio * Math.sin(angle),
              });

              const faceColor = { safety: '#4C9BB8', profit: '#f5c518', future: '#e54848' };

              // 面向中間角度（用於外圍大標籤）
              const faceMid = {
                safety: start + 1.5 * step,  // items 0-3 中間
                profit: start + 6.0 * step,  // items 4-8 中間
                future: start + 10.0 * step, // items 9-11 中間
              };
              const FACE_LABELS = {
                safety: '🛡️ 安全性',
                profit: '💰 獲利性',
                future: '🚀 未來性',
              };

              const rings = [2, 4, 6, 8, 10];
              const ringPoly = ratio => angles.map(a => {
                const p = pt(a, ratio / 10);
                return `${p.x},${p.y}`;
              }).join(' ');

              const polyPts = vals.map((v, i) => {
                const p = pt(angles[i], v / 10);
                return `${p.x},${p.y}`;
              }).join(' ');

              return (
                <svg width={440} height={440} viewBox="0 0 440 440">
                  {/* 背景同心多邊形 */}
                  {rings.map(r => (
                    <polygon key={r} points={ringPoly(r)} fill="none" stroke="#1a2a3a" strokeWidth={1} />
                  ))}
                  {/* 刻度數字 */}
                  {rings.map(r => (
                    <text key={r} x={cx + 4} y={cy - R * r / 10 + 4} fill="#2a3a4a" fontSize={9} textAnchor="start">
                      {r}
                    </text>
                  ))}
                  {/* 軸線 */}
                  {angles.map((a, i) => {
                    const p = pt(a, 1);
                    return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#1a2a3a" strokeWidth={1} />;
                  })}
                  {/* 分數填充 */}
                  <polygon points={polyPts} fill="rgba(245,197,24,0.15)" stroke="#FFD700" strokeWidth={1.5} />
                  {/* 12 個頂點圓點 */}
                  {vals.map((v, i) => {
                    const p = pt(angles[i], v / 10);
                    return <circle key={i} cx={p.x} cy={p.y} r={3.5} fill={faceColor[ITEMS12[i].face]} stroke="#0d1520" strokeWidth={1.5} />;
                  })}
                  {/* 12 個項目標籤（短名 + 分數）*/}
                  {ITEMS12.map((item, i) => {
                    const a = angles[i];
                    const lp = pt(a, 1.22);
                    const anchor = lp.x < cx - 8 ? 'end' : lp.x > cx + 8 ? 'start' : 'middle';
                    return (
                      <g key={i}>
                        <text x={lp.x} y={lp.y - 5} fill={faceColor[item.face]} fontSize={9.5} textAnchor={anchor}>{item.short}</text>
                        <text x={lp.x} y={lp.y + 7} fill={scoreColor(vals[i])} fontSize={11} fontWeight={700} textAnchor={anchor}
                          style={{ fontFamily: 'monospace' }}>{vals[i] > 0 ? vals[i].toFixed(1) : '–'}</text>
                      </g>
                    );
                  })}
                  {/* 三大面向大標籤（最外圍）*/}
                  {Object.entries(faceMid).map(([face, a]) => {
                    const lp = pt(a, 1.52);
                    const anchor = lp.x < cx - 20 ? 'end' : lp.x > cx + 20 ? 'start' : 'middle';
                    return (
                      <text key={face} x={lp.x} y={lp.y + 4} fill={faceColor[face]} fontSize={13} fontWeight={800} textAnchor={anchor}>
                        {FACE_LABELS[face]}
                      </text>
                    );
                  })}
                  {/* 中心總分 */}
                  <text x={cx} y={cy + 7} fill={scoreColor(summary.total)} fontSize={20} fontWeight={800}
                    textAnchor="middle" style={{ fontFamily: 'monospace' }}>
                    {summary.total?.toFixed(2) || '–'}
                  </text>
                </svg>
              );
            })()}
          </div>

          {/* 總分 + 三大面向（雷達圖下方）*/}
          <div style={{
            marginTop: 16, paddingTop: 16, borderTop: '1px solid #1e2a3a',
            display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', justifyContent: 'center',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#7ab0cc', letterSpacing: 2, marginBottom: 4 }}>WEIGHTED TOTAL</div>
              <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1, color: scoreColor(summary.total), fontFamily: 'monospace' }}>
                {summary.total?.toFixed(2) || '–'}
              </div>
              <div style={{ fontSize: 13, color: scoreColor(summary.total), marginTop: 4 }}>
                {scoreLevel(summary.total)}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {['safety','profit','future'].map(face => {
                const meta = FACE_META[face];
                const s = summary[face];
                return (
                  <div key={face} style={{
                    padding: '12px 20px', borderRadius: 10,
                    background: `linear-gradient(135deg, ${meta.bgGlow}, transparent)`,
                    border: `1px solid ${meta.color}40`,
                    minWidth: 140,
                  }}>
                    <div style={{ fontSize: 12, color: meta.color, fontWeight: 600, marginBottom: 4, letterSpacing: 1 }}>
                      {meta.icon} {meta.label}
                    </div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: scoreColor(s), fontFamily: 'monospace', lineHeight: 1 }}>
                      {s?.toFixed(2) || '–'}
                    </div>
                    <div style={{ fontSize: 10, color: '#7ab0cc', marginTop: 2 }}>權重 {weights[face]}%</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 風險告知 */}
          <div style={{
            marginTop: 14, padding: 12, borderRadius: 6,
            background: 'rgba(245, 197, 24, 0.08)', border: '1px solid rgba(245, 197, 24, 0.3)',
            fontSize: 12, color: '#d8c878', lineHeight: 1.7,
          }}>
            ⚠️ 股價的漲跌包含各種因素，非單一因素可以決定，本系統僅就財務的三大面向評估，
            分數的高無法判定股價的高低，請使用者注意。
          </div>
        </div>
      )}

      {/* 載入中 */}
      {loading && !scoreData && (
        <AILoadingIndicator
          label="AI 評分中"
          estimatedSeconds={12}
          stages={['讀取財務資料', '套用 12 項指標', '計算三大面向', '產出綜合評分']}
        />
      )}

      {/* 沒選股票：顯示評分制度說明 + 財務評分排行榜 */}
      {!selectedTicker && (
        <>
          {/* 引導提示 */}
          <div style={{
            marginBottom: 16, padding: '14px 18px', borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(76,155,184,0.12), transparent)',
            border: '1px solid #1e2a3a', color: '#a8c0d0', fontSize: 14, lineHeight: 1.7,
          }}>
            👆 在上方搜尋一檔股票，即可看到 AI 對它的「三大面向 12 項」完整評分。
            以下先說明每一項指標評估的內容：
          </div>

          {/* 評分制度說明：三大面向 12 項（公版說明，無分數）*/}
          {['safety', 'profit', 'future'].map(face => {
            const meta = FACE_META[face];
            const items = ITEMS[face];
            return (
              <div key={face} style={{
                marginBottom: 16, background: '#0d1520',
                border: `1px solid ${meta.color}40`, borderRadius: 10, padding: 16,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${meta.color}30`,
                }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: meta.color }}>
                    {meta.icon} {meta.label}
                  </div>
                  <div style={{ color: '#4a6878', fontFamily: 'monospace', fontSize: 13 }}>
                    {items.length} 項指標
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
                  {items.map(item => (
                    <div key={item.key} style={{
                      background: '#080d14', borderRadius: 8, padding: 14,
                      border: '1px solid #1a2535',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#ddd' }}>{item.name}</div>
                        <div style={{
                          fontSize: 11, color: '#4a6878', fontFamily: 'monospace',
                          border: '1px solid #1a2535', borderRadius: 4, padding: '2px 8px',
                        }}>
                          待評分
                        </div>
                      </div>
                      <div style={{ color: '#d2dde6', fontSize: 14, lineHeight: 1.65 }}>
                        {ITEM_DESC[item.key] || ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

        </>
      )}

      {/* 載入失敗 */}
      {selectedTicker && !loading && !scoreData && error && (
        <div style={{ padding: 40, textAlign: 'center', background: '#0d1520', borderRadius: 10 }}>
          <div style={{ color: '#e54848', fontSize: 16, marginBottom: 12 }}>
            <b>{selectedTicker} {selectedName}</b> 載入失敗
          </div>
          <div style={{ color: '#7ab0cc', fontSize: 13 }}>{error}</div>
        </div>
      )}

      {/* 計分板分數 + 規則明細 */}
      {scoreData && !loading && (
        <>
          {/* 觸發的投資規則明細（按三大面向分組）*/}
          {['safety','profit','future'].map(face => {
            const meta = FACE_META[face];
            const faceScore = summary?.[face];
            // 篩出此面向的規則，加分由大到小排序
            const rules = (scoreData.rule_adjustments || [])
              .filter(r => r.aspect === face)
              .sort((a, b) => (b.adj || 0) - (a.adj || 0));

            return (
              <div key={face} style={{
                marginBottom: 16, background: '#0d1520',
                border: `1px solid ${meta.color}40`, borderRadius: 10, padding: 16,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${meta.color}30`,
                }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: meta.color }}>
                    {meta.icon} {meta.label}
                  </div>
                  <div style={{ color: scoreColor(faceScore), fontFamily: 'monospace', fontSize: 22, fontWeight: 800 }}>
                    {faceScore?.toFixed(2) ?? '–'}
                  </div>
                </div>

                {rules.length === 0 ? (
                  <div style={{ color: '#5a8090', fontSize: 13, fontStyle: 'italic', padding: '12px 4px' }}>
                    無觸發規則
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {rules.map((r, idx) => {
                      const isPos = r.adj > 0;
                      const barColor = isPos ? '#22c55e' : '#e54848';
                      const bgColor = isPos ? 'rgba(34,197,94,0.08)' : 'rgba(229,72,72,0.08)';
                      return (
                        <div key={idx} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '8px 12px', background: bgColor,
                          borderLeft: `3px solid ${barColor}`, borderRadius: 0,
                        }}>
                          <div style={{
                            fontFamily: 'monospace', fontSize: 15, fontWeight: 800,
                            color: barColor, minWidth: 48, textAlign: 'right',
                          }}>
                            {isPos ? '+' : ''}{r.adj}
                          </div>
                          <div style={{ color: '#d2dde6', fontSize: 13, flex: 1, lineHeight: 1.5 }}>
                            {r.reason}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* 資料更新時間 */}
          {scoreData.updated_at && (
            <div style={{ textAlign: 'center', color: '#4a6878', fontSize: 11, padding: 8 }}>
              資料更新：{scoreData.updated_at.slice(0, 19).replace('T', ' ')}　評分機制：全市場 PR 排名 + 24 條投資規則加減分
            </div>
          )}
        </>
      )}

      {/* ── 杜邦分析：同類股 ROE 比較 ── */}
      {selectedTicker && (dupontLoading || dupont) && (
        <div style={{ marginTop: 16, background: '#0d1520', border: '1px solid #2a3a4a', borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#7ab0cc', marginBottom: 4 }}>
            📐 類股 ROE 杜邦分析
          </div>
          {dupont?.sub_industry && (
            <div style={{ fontSize: 12, color: '#5a8090', marginBottom: 12 }}>
              {dupont.sub_industry}
            </div>
          )}

          {dupontLoading && (
            <div style={{ color: '#5a8090', padding: '20px 0' }}>⏳ 載入同類股資料...</div>
          )}

          {!dupontLoading && dupont?.no_peers && (
            <div style={{ color: '#5a8090', padding: '12px 0', fontStyle: 'italic' }}>
              此類股僅 {dupont.name} 一家，無同業可供比較。
            </div>
          )}

          {!dupontLoading && dupont?.peers?.length > 0 && (
            <>
              {/* 公式說明 */}
              <div style={{
                fontSize: 11, color: '#5a8090', marginBottom: 10, padding: '8px 10px',
                background: '#060910', borderRadius: 6, lineHeight: 2,
              }}>
                ROE（股東權益報酬率）＝ 稅後淨利率（獲利能力）× 資產週轉率（經營效率）× 權益乘數（財務槓桿）
              </div>

              <div style={{ overflowX: 'auto' }}>
                {(() => {
                  const peers = dupont.peers;
                  // 各欄最高值
                  const maxRoe   = Math.max(...peers.map(p => p.roe ?? -Infinity));
                  const maxNm    = Math.max(...peers.map(p => p.net_margin ?? -Infinity));
                  const maxAt    = Math.max(...peers.map(p => p.asset_turnover ?? -Infinity));
                  const maxEm    = Math.max(...peers.map(p => p.equity_multiplier ?? -Infinity));
                  const isMax = (val, mx) => val != null && val === mx;

                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, fontFamily: 'monospace', minWidth: 680 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #2a3a4a', background: '#080d14' }}>
                          <th style={{ padding: '9px 10px', textAlign: 'left',  color: '#7ab0cc' }}>股名</th>
                          <th style={{ padding: '9px 10px', textAlign: 'right', color: '#7ab0cc' }}>股價</th>
                          <th style={{ padding: '9px 10px', textAlign: 'right', color: '#FFD700', fontWeight: 700 }}>ROE %</th>
                          <th style={{ padding: '9px 10px', textAlign: 'right', color: '#7ab0cc' }}>稅後淨利率 %</th>
                          <th style={{ padding: '9px 10px', textAlign: 'right', color: '#7ab0cc' }}>資產週轉率</th>
                          <th style={{ padding: '9px 10px', textAlign: 'right', color: '#7ab0cc' }}>權益乘數</th>
                        </tr>
                      </thead>
                      <tbody>
                        {peers.map(p => (
                          <tr key={p.ticker} style={{
                            borderBottom: '1px solid #131c26',
                            background: p.is_target ? 'rgba(76,155,184,0.1)' : 'transparent',
                          }}>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ color: '#4C9BB8', marginRight: 6, fontSize: 13 }}>{p.ticker}</span>
                              <span style={{ color: p.is_target ? '#fff' : '#c0d8ea', fontWeight: p.is_target ? 700 : 400 }}>{p.name}</span>
                              {p.is_target && <span style={{ marginLeft: 6, fontSize: 10, color: '#4C9BB8' }}>◀</span>}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', color: '#c0d8ea', fontSize: 15 }}>
                              {p.close != null ? p.close.toLocaleString() : '-'}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 17, fontWeight: 800,
                              color: isMax(p.roe, maxRoe) ? '#FFD700' : (p.roe == null ? '#555' : (p.roe >= 15 ? '#22c55e' : '#7ab0cc')) }}>
                              {p.roe != null ? p.roe.toFixed(2) + '%' : '-'}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 16, fontWeight: 600,
                              color: isMax(p.net_margin, maxNm) ? '#FFD700' : '#c0d8ea' }}>
                              {p.net_margin != null ? p.net_margin.toFixed(2) + '%' : '-'}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 16, fontWeight: 600,
                              color: isMax(p.asset_turnover, maxAt) ? '#FFD700' : '#c0d8ea' }}>
                              {p.asset_turnover != null ? p.asset_turnover.toFixed(2) + '次' : '-'}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 16, fontWeight: 600,
                              color: isMax(p.equity_multiplier, maxEm) ? '#FFD700' : '#c0d8ea' }}>
                              {p.equity_multiplier != null ? p.equity_multiplier.toFixed(2) + '倍' : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              {/* 風險提醒 */}
              <div style={{ fontSize: 11, color: '#4a6878', marginTop: 10 }}>
                ＊資產週轉率為最近 4 季營收合計÷最新季資產總額（年化），ROE 同邏輯。數據來源：季報。
              </div>
            </>
          )}
        </div>
      )}
    {/* ── 全市場 PR 排行榜 ── */}
    <div style={{ marginTop: 32, padding: 16, background: '#0d1520', border: '1px solid #2a3a4a', borderRadius: 10 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#7ab0cc', marginBottom: 16 }}>
        🏆 全市場 PR 排行榜（每日 07:00 自動更新）
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { k: 'total',  n: '總分',   c: '#FFD700' },
          { k: 'safety', n: '安全性', c: '#4ade80' },
          { k: 'profit', n: '獲利性', c: '#60a5fa' },
          { k: 'future', n: '未來性', c: '#c084fc' },
        ].map(t => (
          <button key={t.k} onClick={() => setRankAspect(t.k)}
            style={{
              padding: '8px 16px', cursor: 'pointer',
              background: rankAspect === t.k ? t.c : 'transparent',
              color: rankAspect === t.k ? '#000' : t.c,
              border: `1px solid ${t.c}`, borderRadius: 6,
              fontWeight: 700, fontSize: 14,
            }}>{t.n}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setRankSide('top')}
          style={{
            padding: '6px 14px', cursor: 'pointer',
            background: rankSide === 'top' ? '#22c55e' : 'transparent',
            color: rankSide === 'top' ? '#000' : '#22c55e',
            border: '1px solid #22c55e', borderRadius: 6,
            fontWeight: 600, fontSize: 13,
          }}>▲ 前 100 名</button>
        <button onClick={() => setRankSide('bottom')}
          style={{
            padding: '6px 14px', cursor: 'pointer',
            background: rankSide === 'bottom' ? '#ef4444' : 'transparent',
            color: rankSide === 'bottom' ? '#fff' : '#ef4444',
            border: '1px solid #ef4444', borderRadius: 6,
            fontWeight: 600, fontSize: 13,
          }}>▼ 後 100 名</button>
      </div>

      {!rankings && (
        <div style={{ color: '#666', padding: 20, textAlign: 'center' }}>載入中⋯</div>
      )}
      {rankings && (() => {
        const list = rankings[rankAspect]?.[rankSide] || [];
        const scoreKey = `${rankAspect}_score`;
        const fmtNum = (n, d = 2) => (n == null || isNaN(n)) ? '-' : Number(n).toFixed(d);
        const chgColor = (n) => (n == null || isNaN(n)) ? '#888' : (n > 0 ? '#ef4444' : (n < 0 ? '#22c55e' : '#888'));
        return (
          <div style={{ maxHeight: 560, overflowY: 'auto', border: '1px solid #1e2a3a', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'JetBrains Mono, monospace' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#0d1520', zIndex: 1 }}>
                <tr style={{ borderBottom: '1px solid #2a3a4a' }}>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: '#7ab0cc', width: '3%' }}>★</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: '#7ab0cc', width: '3%' }}>#</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', color: '#7ab0cc', width: '6%' }}>代號</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', color: '#7ab0cc', width: '13%' }}>名稱</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', color: '#7ab0cc', width: '8%' }}>股價</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', color: '#7ab0cc', width: '7%' }}>漲跌</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', color: '#7ab0cc', width: '8%' }}>漲跌%</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', color: '#7ab0cc', width: '15%' }}>產業</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', color: '#7ab0cc', width: '8%' }}>毛利率</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', color: '#7ab0cc', width: '10%' }}>月營收YoY</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', color: '#7ab0cc', width: '10%' }}>市值(億)</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', color: '#7ab0cc', width: '9%' }}>分數</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r, i) => {
                  const inWl = watchlistSet.has(r.ticker);
                  const chg = r.price_change_1d_abs;
                  const chgPct = r.price_change_1d;
                  return (
                    <tr key={r.ticker}
                        style={{ borderBottom: '1px solid #131c26' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#162030'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}
                          onClick={(e) => { e.stopPropagation(); toggleWatchlist(r.ticker, r.name); }}>
                        <span style={{ cursor: 'pointer', color: inWl ? '#f5c518' : '#3a4a5a', fontSize: 16, userSelect: 'none' }}>
                          {inWl ? '★' : '☆'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 6px', textAlign: 'right', color: '#7ab0cc' }}
                          onClick={() => { setSelectedTicker(r.ticker); setSelectedName(r.name); }}
                          >{i + 1}</td>
                      <td style={{ padding: '6px 8px', color: '#4C9BB8', fontWeight: 700, cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif" }}
                          onClick={() => { setSelectedTicker(r.ticker); setSelectedName(r.name); }}
                          >{r.ticker}</td>
                      <td style={{ padding: '6px 8px', color: '#ddd', cursor: 'pointer' }}
                          onClick={() => { setSelectedTicker(r.ticker); setSelectedName(r.name); }}
                          >{r.name}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ddd' }}>
                        {fmtNum(r.close_now, 2)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: chgColor(chg) }}>
                        {chg == null || isNaN(chg) ? '-' : (chg > 0 ? '+' : '') + Number(chg).toFixed(2)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: chgColor(chgPct), fontWeight: 600 }}>
                        {chgPct == null || isNaN(chgPct) ? '-' : (chgPct > 0 ? '+' : '') + Number(chgPct).toFixed(2) + '%'}
                      </td>
                      <td style={{ padding: '6px 8px', color: '#aaa', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                        {r.new_category || '-'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ddd' }}>
                        {r.gross_margin_q == null ? '-' : Number(r.gross_margin_q).toFixed(1) + '%'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: chgColor(r.mrev_yoy), fontWeight: 600 }}>
                        {r.mrev_yoy == null ? '-' : (r.mrev_yoy > 0 ? '+' : '') + Number(r.mrev_yoy).toFixed(2) + '%'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ddd' }}>
                        {r.market_cap == null ? '-' : Math.round(r.market_cap).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#fff', fontWeight: 700 }}>
                        {r[scoreKey] != null ? Number(r[scoreKey]).toFixed(2) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>

    </div>
  );
}
